import React, { useState } from "react";
import { ShieldAlert, ExternalLink, Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import { useAuth } from "../../context/AuthContext";
import { authApi, errorMessage } from "../../lib/api";
import { toast } from "sonner";

const POLICIES = [
  { to: "/terms", label: "Terms & Conditions" },
  { to: "/charges", label: "Charges & Fees" },
  { to: "/refunds", label: "Refund & Cancellation Policy" },
  { to: "/privacy", label: "Privacy Policy" },
];

// Shown to a signed-in user when our legal/fare terms have changed since they
// last accepted. Rendered as a top-most fixed overlay so it sits above every
// other modal (e.g. the location gate) and blocks the app until re-accepted.
export default function ReConsentGate() {
  const { user, setUser } = useAuth();
  const [busy, setBusy] = useState(false);
  const needs = !!user && user.terms_version && user.terms_accepted_version !== user.terms_version;

  const accept = async () => {
    setBusy(true);
    try {
      const u = await authApi.acceptTerms();
      setUser(u.user || u);
      toast.success("Thanks — you're all set");
    } catch (e) { toast.error(errorMessage(e, "Could not save your acceptance")); } finally { setBusy(false); }
  };

  if (!needs) return null;

  return (
    <div className="pointer-events-auto fixed inset-0 z-[9999] flex items-center justify-center bg-ink/60 p-4 backdrop-blur-sm" data-testid="reconsent-modal">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
        <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-50 text-amber-600"><ShieldAlert className="h-6 w-6" /></div>
        <h2 className="text-xl font-bold text-ink">We've updated our terms</h2>
        <p className="mt-2 text-sm text-ink-soft">Our Charges &amp; Fees and policies have changed. Please review and accept the latest version to keep using KohimaTaxi.</p>
        <ul className="my-3 space-y-2">
          {POLICIES.map((p) => (
            <li key={p.to}>
              <a href={p.to} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between rounded-xl border border-surface-300 px-4 py-3 text-sm font-medium text-ink transition-colors hover:border-brand-200 hover:bg-brand-50" data-testid={`reconsent-link-${p.to.replace("/", "")}`}>
                {p.label}<ExternalLink className="h-4 w-4 text-ink-muted" />
              </a>
            </li>
          ))}
        </ul>
        <p className="text-xs text-ink-muted">By tapping “Accept &amp; continue” you agree to the updated Terms &amp; Conditions, Charges &amp; Fees, Refund &amp; Cancellation Policy and Privacy Policy.</p>
        <Button onClick={accept} disabled={busy} className="mt-3 w-full rounded-full bg-brand text-white hover:bg-brand-600" data-testid="reconsent-accept">
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Accept &amp; continue
        </Button>
      </div>
    </div>
  );
}
